import React, { useEffect, useMemo } from 'react';
import { SortCriterion, useGlobalContext, FlightSortCriterion } from '@honeycomb/data';
import { TypographyBody, Anchor, Option, Stacker, Skeleton, TickIcon, Dialog, DialogContent, DialogHeader, useDisclosure, } from '@honeycomb/ui-core';
export function SortResults({ currentSortValue, sortValueOptions, onSortChange, loading = false, isOneWayFlight = false, }) {
    var _a;
    const { resourceStrings: { SORTBY, RECOMMENDED, PRICECHEAPESTFIRST, CUSTOMERRATINGHIGHESTFIRST, STARRATINGHIGHESTFIRST, PRICENOBRACKETS, CUSTOMERRATINGNOBRACKETS, STARRATINGNOBRACKETS, DURATIONQUICKESTFIRST, DURATIONNOBRACKETS, PRICEHIGHESTFIRST, DEPARTURETIMEEARLIESTFIRST, DEPARTURETIMENOBRACKETS, DEPARTURETIMELATESTFIRST, RETURNTIMEEARLIESTFIRST, RETURNTIMENOBRACKETS, RETURNTIMELATESTFIRST, ARRIVALTIMEEARLIESTFIRST, ARRIVALTIMENOBRACKETS, ARRIVALTIMELATESTFIRST, DURATIONLONGESTFIRST, }, } = useGlobalContext();
    const [dialogOpen, { open: handleOpenDialog, close: handleCloseDialog }] = useDisclosure(false);
    const [dialogExited, setDialogExited] = React.useState(true);
    // We need local state in order to see the radio button change  before the modal closes
    const [localValue, setLocalValue] = React.useState(currentSortValue);
    useEffect(() => {
        if (localValue !== currentSortValue) {
            setLocalValue(currentSortValue);
        }
    }, [localValue, currentSortValue]);
    const handleOpen = () => {
        handleOpenDialog();
        setDialogExited(false);
    };
    const masterSort = useMemo(() => {
        return {
            [SortCriterion.RecommendedHighestFirst]: {
                name: RECOMMENDED,
                shortName: RECOMMENDED,
            },
            [SortCriterion.PriceLowestFirst]: {
                name: PRICECHEAPESTFIRST,
                shortName: PRICENOBRACKETS,
            },
            [SortCriterion.PriceHighestFirst]: {
                name: PRICEHIGHESTFIRST,
                shortName: PRICENOBRACKETS,
            },
            [SortCriterion.CustomerRatingHighestFirst]: {
                name: CUSTOMERRATINGHIGHESTFIRST,
                shortName: CUSTOMERRATINGNOBRACKETS,
            },
            [SortCriterion.StarRatingHighestFirst]: {
                name: STARRATINGHIGHESTFIRST,
                shortName: STARRATINGNOBRACKETS,
            },
            [SortCriterion.DurationLowestFirst]: {
                name: DURATIONQUICKESTFIRST,
                shortName: DURATIONNOBRACKETS,
            },
            [FlightSortCriterion.DepartureTimeEarliestFirst]: {
                name: DEPARTURETIMEEARLIESTFIRST,
                shortName: DEPARTURETIMENOBRACKETS,
            },
            [FlightSortCriterion.DepartureTimeLatestFirst]: {
                name: DEPARTURETIMELATESTFIRST,
                shortName: DEPARTURETIMENOBRACKETS,
            },
            [FlightSortCriterion.ReturnTimeEarliestFirst]: {
                name: isOneWayFlight ? ARRIVALTIMEEARLIESTFIRST : RETURNTIMEEARLIESTFIRST,
                shortName: isOneWayFlight ? ARRIVALTIMENOBRACKETS : RETURNTIMENOBRACKETS,
            },
            [FlightSortCriterion.ReturnTimeLatestFirst]: {
                name: isOneWayFlight ? ARRIVALTIMELATESTFIRST : RETURNTIMELATESTFIRST,
                shortName: isOneWayFlight ? ARRIVALTIMENOBRACKETS : RETURNTIMENOBRACKETS,
            },
            [FlightSortCriterion.DurationQuickestFirst]: {
                name: DURATIONQUICKESTFIRST,
                shortName: DURATIONNOBRACKETS,
            },
            [FlightSortCriterion.DurationLongestFirst]: {
                name: DURATIONLONGESTFIRST,
                shortName: DURATIONNOBRACKETS,
            },
        };
    }, [
        ARRIVALTIMEEARLIESTFIRST,
        ARRIVALTIMELATESTFIRST,
        ARRIVALTIMENOBRACKETS,
        CUSTOMERRATINGHIGHESTFIRST,
        CUSTOMERRATINGNOBRACKETS,
        DEPARTURETIMEEARLIESTFIRST,
        DEPARTURETIMELATESTFIRST,
        DEPARTURETIMENOBRACKETS,
        DURATIONLONGESTFIRST,
        DURATIONNOBRACKETS,
        DURATIONQUICKESTFIRST,
        PRICECHEAPESTFIRST,
        PRICEHIGHESTFIRST,
        PRICENOBRACKETS,
        RECOMMENDED,
        RETURNTIMEEARLIESTFIRST,
        RETURNTIMELATESTFIRST,
        RETURNTIMENOBRACKETS,
        STARRATINGHIGHESTFIRST,
        STARRATINGNOBRACKETS,
        isOneWayFlight,
    ]);
    const handleChange = React.useCallback((newValue) => (_event) => {
        setLocalValue(newValue);
        onSortChange(newValue);
        handleCloseDialog();
    }, [handleCloseDialog, onSortChange]);
    if (loading) {
        return (React.createElement(TypographyBody, { component: "div", w: 160 },
            React.createElement(Skeleton, { variant: "text" })));
    }
    return (React.createElement(React.Fragment, null,
        React.createElement(Stacker, { direction: "row", alignItems: "center", spacing: 1, "data-id": "sorting" },
            React.createElement(TypographyBody, { weight: "semiBold", size: "s" },
                SORTBY,
                ":"),
            React.createElement(Anchor, { "data-id": "sort-by-selected-options", component: "button", underline: "always", onClick: handleOpen, color: "text.link", weight: "semiBold", size: "s" }, ((_a = masterSort[currentSortValue]) === null || _a === void 0 ? void 0 : _a.shortName) || RECOMMENDED)),
        (dialogOpen || !dialogExited) && (React.createElement(Dialog, { open: dialogOpen, onClose: handleCloseDialog, size: "s", onExited: () => setDialogExited(true) },
            React.createElement(DialogHeader, { withCloseButton: true, border: false }, SORTBY),
            React.createElement(DialogContent, { disableGutters: true, px: 1, pb: 4 },
                React.createElement(Stacker, { component: "ul", gap: 1 }, Object.entries(masterSort)
                    .filter(([key]) => sortValueOptions.includes(key))
                    .map(([key, val]) => {
                    const selected = localValue === key;
                    return (React.createElement("li", { key: `sortOptions-${val.name}` },
                        React.createElement(Option, { button: true, selected: localValue === key, onClick: handleChange(key), actionIcon: selected ? React.createElement(TickIcon, null) : undefined, "data-id": `sort-options-${val.name}` }, val.name)));
                })))))));
}
